import { useEffect, useState } from "react";
import { AlertTriangle, BadgeCheck, Loader2, Plus, WalletCards } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { commissionCatalogApi, type CommissionType } from "@/lib/commission-catalog.api";
import { EditCommissionTypeDialog } from "@/pages/commission/EditCommissionTypeDialog";
import { LoadingLine } from "../components/LoadingLine";
import { SettingsKpi } from "../components/SettingsKpi";
import { getApiErrorMessage } from "../settingsPage.helpers";

export function CatalogTab() {
  const [types, setTypes] = useState<CommissionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  function reload() {
    commissionCatalogApi.list().then((response) => {
      setTypes(response.data);
      setLoading(false);
    });
  }

  useEffect(reload, []);

  async function handleToggle(code: string, active: boolean) {
    setTypes((current) => current.map((type) => (type.code === code ? { ...type, active } : type)));
    await commissionCatalogApi.toggleActive(code, active);
  }

  async function handleCreate() {
    setCreateError(null);
    setCreating(true);
    try {
      await commissionCatalogApi.create({ code: newCode, label: newLabel });
      setDialogOpen(false);
      setNewCode("");
      setNewLabel("");
      reload();
    } catch (error: unknown) {
      setCreateError(getApiErrorMessage(error, "Erreur lors de la création."));
    } finally {
      setCreating(false);
    }
  }

  if (loading) return <LoadingLine label="Chargement du catalogue..." />;

  const activeCount = types.filter((type) => type.active).length;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-3">
        <SettingsKpi icon={WalletCards} label="Types de commission" value={String(types.length)} />
        <SettingsKpi icon={BadgeCheck} label="Types actifs" value={String(activeCount)} />
        <SettingsKpi
          icon={AlertTriangle}
          label="Types désactivés"
          value={String(types.length - activeCount)}
          tone="warning"
        />
      </div>

      <section className="border border-border bg-card">
        <div className="grid gap-3 border-b border-border px-4 py-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Catalogue des commissions</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Ces types structurent la saisie, les filtres et les graphiques de commissions.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger>
              <Button size="sm">
                <Plus size={14} /> Nouveau type
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouveau type de commission</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="mb-1.5 block text-[11.5px] font-medium text-body">
                    Identifiant interne
                  </label>
                  <Input
                    value={newCode}
                    onChange={(event) => setNewCode(event.target.value)}
                    placeholder="partenariat_hotel"
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Utilisé pour relier ce type aux rapports. Préférez minuscules, chiffres et
                    tirets bas.
                  </p>
                </div>
                <div>
                  <label className="mb-1.5 block text-[11.5px] font-medium text-body">
                    Nom affiché
                  </label>
                  <Input
                    value={newLabel}
                    onChange={(event) => setNewLabel(event.target.value)}
                    placeholder="Partenariat hôtel"
                  />
                </div>
                {createError && <p className="text-[11px] text-danger-text">{createError}</p>}
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleCreate} disabled={creating || !newCode || !newLabel}>
                  {creating ? <Loader2 size={13} className="animate-spin" /> : "Créer"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-3 p-3 md:grid-cols-2 xl:grid-cols-4">
          {types.map((type) => (
            <div
              key={type.code}
              className={`flex min-h-full flex-col justify-between border p-3 ${
                type.active
                  ? "border-success-border bg-success-bg/40"
                  : "border-border bg-surface-muted"
              }`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-foreground">{type.label}</p>
                  <span
                    className={`border px-2 py-0.5 text-[11px] ${
                      type.active
                        ? "border-success-border bg-success-bg text-success-text"
                        : "border-border bg-surface-muted text-muted-foreground"
                    }`}
                  >
                    {type.active ? "Actif" : "Désactivé"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Identifiant interne : {type.code}
                </p>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-dashed border-border pt-3">
                <EditCommissionTypeDialog type={type} onUpdated={reload} />
                <Switch
                  checked={type.active}
                  onCheckedChange={(value) => handleToggle(type.code, value)}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
